import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { VacationRequestStatus } from '../../types';

interface VacationStatusBadgeProps {
  status: VacationRequestStatus | string;
}

const statusStyle: Record<string, string> = {
  [VacationRequestStatus.PENDING]: 'bg-amber-100 text-amber-800',
  [VacationRequestStatus.APPROVED]: 'bg-green-100 text-green-800',
  [VacationRequestStatus.REJECTED]: 'bg-red-100 text-red-800',
  [VacationRequestStatus.CANCELLED]: 'bg-muted text-muted-foreground',
};

const statusKey: Record<string, string> = {
  [VacationRequestStatus.PENDING]: 'pending',
  [VacationRequestStatus.APPROVED]: 'approved',
  [VacationRequestStatus.REJECTED]: 'rejected',
  [VacationRequestStatus.CANCELLED]: 'cancelled',
};

/**
 * Single reusable status pill for vacation requests. Colour and label are
 * driven entirely by the `status` prop.
 */
export const VacationStatusBadge: React.FC<VacationStatusBadgeProps> = ({ status }) => {
  const { t } = useTranslation('vacations');
  const style = statusStyle[status] ?? 'bg-muted text-muted-foreground';
  const key = statusKey[status] ?? status;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        style,
      )}
      data-testid={`vacation-status-${status}`}
    >
      {t(`status.${key}`)}
    </span>
  );
};
